<template>
  <div class="review-rows">
        <!-- Closed until asked for, and not built until opened: a count and a sentence are the whole
             summary, and the rows behind them are for the one group the operator decides to open. -->
        <BaseTable
          class="review-section__table"
          :data="rows"
          :width="'100%'"
          :hover-active="false"
          :row-class="api.reviewRowClass"
        >
          <BaseTableColumn v-if="!unable" style="width: 52px" name="select"></BaseTableColumn>
          <BaseTableColumn style="width: 48px" name="line">
            {{ api.runText('paymentsReview.headers.line') }}
          </BaseTableColumn>
          <BaseTableColumn style="width: 150px" name="policy">
            {{ api.runText('paymentsReview.headers.policy') }}
          </BaseTableColumn>
          <BaseTableColumn style="width: 170px" name="insured">
            {{ api.runText('paymentsReview.headers.insured') }}
          </BaseTableColumn>
          <!-- A number several policies carry: one column per candidate policy, so each can be opened and
               the duplicate found. At most three -- the third lists any beyond it. -->
          <BaseTableColumn v-if="candidateColumns >= 1" style="width: 170px" name="candidate1">
            {{ candidateHeader(1) }}
          </BaseTableColumn>
          <BaseTableColumn v-if="candidateColumns >= 2" style="width: 170px" name="candidate2">
            {{ candidateHeader(2) }}
          </BaseTableColumn>
          <BaseTableColumn v-if="candidateColumns >= 3" style="width: 170px" name="candidate3">
            {{ candidateHeader(3) }}
          </BaseTableColumn>
          <BaseTableColumn v-if="showsPayment" style="width: 170px" name="inRenova">
            {{ api.runText('paymentsReview.headers.inRenova') }}
          </BaseTableColumn>
          <BaseTableColumn style="width: 170px" name="inFile">
            {{ api.runText('paymentsReview.headers.inFile') }}
          </BaseTableColumn>
          <!-- What the payment becomes once applied. -->
          <BaseTableColumn v-if="showsPayment" style="width: 180px" name="after">
            {{ api.runText('paymentsReview.headers.after') }}
          </BaseTableColumn>
          <BaseTableColumn v-if="showsPayment && !api.isCancelRun" style="width: 140px" name="difference">
            {{ api.runText('paymentsReview.headers.difference') }}
          </BaseTableColumn>
          <BaseTableColumn v-if="unable" style="width: 520px" name="why">
            {{ api.runText('paymentsReview.headers.why') }}
          </BaseTableColumn>
          <BaseTableColumn v-if="!unable" :style="{ width: actionWidth }" name="action">
            {{ api.runText('paymentsReview.headers.action') }}
          </BaseTableColumn>

          <!-- The row's whole state, in one control: checked applies, unchecked does not, disabled cannot --
               with the reason on the red mark beside it. -->
          <template #select="{ row }">
            <div style="width: 52px" class="review-section__table-cell review-select">
              <el-checkbox
                v-if="api.showsCheckbox(row)"
                :value="api.isChecked(row)"
                :disabled="api.isBlocked(row)"
                @change="api.toggleSelect(row)"
              />
              <!-- The app's tooltip rather than a native title: a title waits a second and a half to appear,
                   and on this mark it read as a bare "?" cursor with nothing behind it. -->
              <BaseTooltip
                v-if="api.isBlocked(row)"
                class="review-blocked-tooltip"
                :tooltip-text="api.blockedText(row)"
                placement="top-start"
              >
                <span class="review-blocked-icon" role="img" :aria-label="api.blockedText(row)">
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" fill="currentColor"/>
                    <rect x="11" y="6" width="2" height="8" rx="1" fill="#FFFFFF"/>
                    <rect x="11" y="16" width="2" height="2" rx="1" fill="#FFFFFF"/>
                  </svg>
                </span>
              </BaseTooltip>
            </div>
          </template>

          <template #line="{ row }">
            <div style="width: 48px" class="review-section__table-cell review-muted">
              {{ Number.isInteger(row.rowNumber) ? row.rowNumber + api.fileRowOffset : '—' }}
            </div>
          </template>

          <template #policy="{ row }">
            <PolicyNumberCell :row="row" />
          </template>

          <template #insured="{ row }">
            <div style="width: 170px" class="review-section__table-cell review-section__table-cell--stacked">
              <span :title="api.insuredName(row)">{{ api.insuredName(row) }}</span>
              <span v-if="nameMatchBadge(row)" class="diff-chip diff-chip--neutral review-name-match">
                {{ $t(nameMatchBadge(row)) }}
              </span>
            </div>
          </template>

          <template #candidate1="{ row }">
            <div style="width: 170px" class="review-section__table-cell review-section__table-cell--stacked">
              <template v-for="c in candidatesAt(row, 0)">
                <a
                  :key="c.policyId"
                  :href="`/admin/policies/${c.policyId}`"
                  target="_blank"
                  rel="noopener"
                  class="body-text body-text--link color--purple review-candidate"
                >{{ c.policyNumber || '—' }} ↗</a>
                <span v-if="holderDiffers(row, c)" :key="`${c.policyId}-holder`" class="review-subline">
                  {{ c.fullName }}
                </span>
              </template>
            </div>
          </template>
          <template #candidate2="{ row }">
            <div style="width: 170px" class="review-section__table-cell review-section__table-cell--stacked">
              <template v-for="c in candidatesAt(row, 1)">
                <a
                  :key="c.policyId"
                  :href="`/admin/policies/${c.policyId}`"
                  target="_blank"
                  rel="noopener"
                  class="body-text body-text--link color--purple review-candidate"
                >{{ c.policyNumber || '—' }} ↗</a>
                <span v-if="holderDiffers(row, c)" :key="`${c.policyId}-holder`" class="review-subline">
                  {{ c.fullName }}
                </span>
              </template>
            </div>
          </template>
          <template #candidate3="{ row }">
            <div style="width: 170px" class="review-section__table-cell review-section__table-cell--stacked">
              <template v-for="c in candidatesAt(row, 2)">
                <a
                  :key="c.policyId"
                  :href="`/admin/policies/${c.policyId}`"
                  target="_blank"
                  rel="noopener"
                  class="body-text body-text--link color--purple review-candidate"
                >{{ c.policyNumber || '—' }} ↗</a>
                <span v-if="holderDiffers(row, c)" :key="`${c.policyId}-holder`" class="review-subline">
                  {{ c.fullName }}
                </span>
              </template>
            </div>
          </template>

          <!-- What the payment says now, and what the file says, in the same shape -- amount then date -- so
               the eye compares them without the row explaining itself. -->
          <template #inRenova="{ row }">
            <div style="width: 170px" class="review-section__table-cell review-section__table-cell--stacked">
              <span v-if="api.renovaNote(row)" class="review-muted">{{ api.renovaNote(row) }}</span>
              <span v-else>{{ api.formatMoney(row.paymentAmount) }} · {{ api.formatDate(row.paymentDate) }}</span>
              <!-- Already paid: what the payment records as its collection, or that it records none. -->
              <span v-if="api.recordedEvidenceLabel(row)" class="review-subline">{{ api.recordedEvidenceLabel(row) }}</span>
              <span v-if="api.nameDiffers(row)" class="review-subline" :title="row.fullName">
                {{ row.fullName || '—' }}
              </span>
              <!-- The operator's pick, when the re-check refused it: still on the row, never replaced. -->
              <span v-if="api.pickedLabel(row)" class="review-subline">{{ api.pickedLabel(row) }}</span>
            </div>
          </template>
          <!-- The file's side. What differs from the payment is red; nothing is green. -->
          <template #inFile="{ row }">
            <div style="width: 170px" class="review-section__table-cell review-section__table-cell--stacked">
              <span>
                <span :class="{ 'review-diff--changed': api.amountMoved(row) }">
                  {{ api.formatMoney(row.csvPaymentAmount) }}
                </span>
                ·
                <span :class="{ 'review-diff--changed': api.dateMoved(row) }">
                  {{ api.formatDate(row.csvPaymentDate) }}
                </span>
              </span>
              <span v-if="api.nameDiffers(row)" class="review-subline review-diff--changed" :title="row.csvFullName">
                {{ row.csvFullName }}
              </span>
            </div>
          </template>

          <template #after="{ row }">
            <div style="width: 180px" class="review-section__table-cell review-section__table-cell--stacked">
              <span>{{ api.afterLabel(row) }}</span>
              <span v-if="api.afterNote(row)" class="review-subline after-label--written-off">
                {{ api.afterNote(row) }}
              </span>
              <!-- Cancellation runs: "cancel what follows" is on, so AFTER says what else goes with this payment. -->
              <span v-if="api.isCancelRun && api.canFollow(row) && api.followsOn(row)" class="review-subline">
                {{ api.runText('paymentsReview.after.following', { count: api.followingOf(row).count }) }}
              </span>
              <!-- Cancellation runs have no DIFFERENCE column, so a blocked row's reason sits here. -->
              <span v-if="api.isCancelRun && api.isBlocked(row)" class="row-blocked-reason">
                {{ api.blockedText(row) }}
              </span>
            </div>
          </template>

          <!-- Only what differs, one fact per line; and, for a row that cannot apply, why. -->
          <template #difference="{ row }">
            <div style="width: 140px" class="review-section__table-cell review-section__table-cell--wrap review-section__table-cell--difference">
              <span v-if="api.differenceParts(row).length" class="diff-chip" :class="api.differenceClass(row)">
                <span v-for="(part, i) in api.differenceParts(row)" :key="i" class="diff-chip__line">{{ part }}</span>
              </span>
              <span v-else class="review-muted">—</span>
              <BaseTooltip v-if="api.tooltipError(row)" :tooltip-text="api.tooltipError(row)" :allow-html="true" />
              <span v-if="api.isBlocked(row)" class="row-blocked-reason">{{ api.blockedText(row) }}</span>
            </div>
          </template>

          <template #why="{ row }">
            <div style="width: 520px" class="review-section__table-cell review-section__table-cell--wrap">
              <span>{{ api.statusText(row) }}</span>
              <BaseTooltip v-if="api.tooltipError(row)" :tooltip-text="api.tooltipError(row)" :allow-html="true" />
            </div>
          </template>

          <template #action="{ row }">
            <div :style="{ width: actionWidth }" class="review-section__table-cell review-section__table-cell--action">
              <!-- Where the row's payment came from: our suggestion, the operator's pick, or one being added. -->
              <span v-if="api.rowStateOf(row)" class="row-state" :class="`row-state--${api.rowStateOf(row)}`">
                {{ api.runText(`paymentsReview.rowState.${api.rowStateOf(row)}`) }}
              </span>
              <div
                v-if="api.canChoose(row)"
                class="review-change-payment color--purple"
                :title="api.runText('paymentsReview.actions.changePaymentTooltip')"
                @click="api.openChooser(row)"
              >
                {{ api.runText('paymentsReview.actions.changePayment') }}
              </div>
              <!-- Cancellation runs: a switch in the row, not a question over the table -- one client stopped
                   paying, another had one receipt reissued, and the answer differs policy by policy. -->
              <div v-if="api.canFollow(row)" class="following-cell">
                <InputSwitcher :checked="api.followsOn(row)" @change="api.toggleFollowing(row)" />
                <span class="following-cell__label">
                  {{ api.runText('paymentsReview.following.label', { count: api.followingOf(row).count }) }}
                </span>
              </div>
            </div>
          </template>
        </BaseTable>

    <!-- The chooser. Built only while a row is open on it -- each one renders a whole policy's
         schedule, and forty mounted at once is the cost this screen exists to avoid.

         Drawn as the policy page's payments table -- same component, same columns in the same order,
         same dots and formats -- so the operator can open the policy beside it and recognise every
         row. A modal rather than an expansion under the row: a long schedule ran off the bottom of the
         panel, taking the buttons that close it with it. -->
    <div
      v-if="api.chooserRow && rows.some((r) => r.rowNumber === api.chooserRow.rowNumber)"
      class="chooser-backdrop"
      @click.self="api.closeChooser()"
    >
      <section class="chooser">
        <header class="chooser__head">
          <p class="chooser__lead body-text--bold color--black-purple">
            {{ api.runText('paymentsReview.chooser.lead', {
              amount: api.moneyWithCurrency(api.chooserRow.csvPaymentAmount, api.chooserRow),
              date: api.formatDate(api.chooserRow.csvPaymentDate),
            }) }}
          </p>
          <div class="chooser__policy body-text color--dark-purple">
            <span>
              {{ api.runText('paymentsReview.chooser.line', { line: api.chooserRow.rowNumber + api.fileRowOffset }) }} ·
              {{ api.chooserRow.policyNumber }} · {{ api.chooserRow.fullName }}
            </span>
            <a
              :href="`/admin/policies/${api.chooserRow.policyId}`"
              target="_blank"
              class="body-text body-text--link color--purple"
            >{{ api.runText('paymentsReview.chooser.openPolicy') }} ↗</a>
            <span
              v-if="api.splitDraft || api.canSplit(api.chooserRow)"
              class="chooser__mode body-text color--purple cursor__pointer"
              @click="api.toggleSplitMode()"
            >
              {{ api.splitDraft ? api.runText('paymentsReview.chooser.splitBack') : api.runText('paymentsReview.chooser.split') }}
            </span>
          </div>
          <p class="body-text color--dark-purple">
            {{ api.splitDraft ? api.runText('paymentsReview.chooser.splitHelp') : api.runText('paymentsReview.chooser.help') }}
          </p>
        </header>

        <div v-if="api.chooserReceipts.length" class="chooser__table">
          <BaseTable
            :data="api.chooserReceipts"
            :width="'100%'"
            :hover-active="false"
            :row-class="api.chooserRowClass"
            @rowClick="api.pickReceipt(api.chooserRow, $event)"
          >
            <BaseTableColumn style="width: 40px" name="pick"></BaseTableColumn>
            <BaseTableColumn style="width: 150px" name="dueDate">{{ $t('policies.PaymentDueDate') }}</BaseTableColumn>
            <BaseTableColumn style="width: 150px" name="paidDate">{{ $t('payments.CollectionDate') }}</BaseTableColumn>
            <BaseTableColumn style="width: 80px" name="days">{{ $t('payments.DateDifferenceDays') }}</BaseTableColumn>
            <BaseTableColumn style="width: 150px" name="amount">{{ $t('adminTitles.Amount') }}</BaseTableColumn>
            <BaseTableColumn style="width: 160px" name="status">{{ $t('policies.paymentStatus') }}</BaseTableColumn>
            <BaseTableColumn name="note"></BaseTableColumn>

            <template #pick="{ row: receipt }">
              <span
                class="chooser__mark"
                :class="{
                  'chooser__mark--box': api.splitDraft,
                  'chooser__mark--on': api.receiptIsPicked(api.chooserRow, receipt),
                }"
              ></span>
            </template>
            <template #dueDate="{ row: receipt }">{{ api.formatDate(receipt.dueDate) }}</template>
            <template #paidDate="{ row: receipt }">
              {{ receipt.status === 'PAID' ? api.formatDate(receipt.paidDate) : '—' }}
            </template>
            <template #days="{ row: receipt }">{{ api.receiptDays(receipt) }}</template>
            <template #amount="{ row: receipt }">{{ api.moneyWithCurrency(receipt.amount, api.chooserRow) }}</template>
            <template #status="{ row: receipt }">
              <span class="chooser__status">
                <span class="chooser__status-dot" :style="{ background: api.statusColor(receipt) }"></span>
                {{ $t(api.statusLabel(receipt)) }}
              </span>
            </template>
            <template #note="{ row: receipt }">
              <span class="review-muted">{{ api.receiptNote(api.chooserRow, receipt) }}</span>
            </template>
          </BaseTable>
        </div>
        <p v-if="!api.chooserHasUnpaid" class="body-text color--dark-purple mb_12">
          {{ api.runText('paymentsReview.chooser.noneUnpaid') }}
        </p>

        <!-- The split: what the ticked payments become, and the one button that commits it. -->
        <div v-if="api.splitDraft" class="chooser__split">
          <p class="body-text color--black-purple">{{ api.splitSummary(api.chooserRow) }}</p>
          <BaseButton
            :is-small="true"
            :disabled="api.splitDraft.length < 2"
            @click="api.chooseSplit(api.chooserRow)"
          >
            {{ api.runText('paymentsReview.chooser.splitUse', { count: api.splitDraft.length }) }}
          </BaseButton>
        </div>

        <template v-else>
          <!-- "None of these." Last, and set apart, because it is the answer of last resort: it adds a
               payment to the policy rather than settling one. -->
          <div
            class="chooser__option"
            :class="{ 'chooser__option--picked': api.createsFor(api.chooserRow) }"
            @click="api.chooseCreate(api.chooserRow)"
          >
            <span class="chooser__mark" :class="{ 'chooser__mark--on': api.createsFor(api.chooserRow) }"></span>
            <span class="body-text color--black-purple">
              {{ api.runText('paymentsReview.chooser.addFromFile', {
                amount: api.moneyWithCurrency(api.chooserRow.csvPaymentAmount, api.chooserRow),
                date: api.formatDate(api.chooserRow.csvPaymentDate),
              }) }}
            </span>
          </div>
          <p class="chooser__caution body-text color--dark-purple">
            {{ api.runText('paymentsReview.chooser.addFromFileNote') }}
          </p>

          <p v-if="api.chooserDebtLabel(api.chooserRow)" class="chooser__debt body-text">
            {{ api.chooserDebtLabel(api.chooserRow) }}
          </p>
        </template>

        <div class="chooser__actions">
          <BaseButton type="outline" :is-small="true" @click="api.closeChooser">
            {{ api.runText('paymentsReview.chooser.close') }}
          </BaseButton>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BaseTable from '@/views/RenovaDashboard/components/ui-kit/BaseTable/BaseTable';
import BaseTableColumn from '@/views/Admin/components/BaseAdminTableColumn';
import BaseTooltip from '@/views/RenovaDashboard/components/ui-kit/BaseTooltip';
import InputSwitcher from '@/views/RenovaDashboard/components/ui-kit/Inputs/InputSwitcher';
import PolicyNumberCell from './PaymentsReviewPolicyNumberCell';
import { nameMatchBadge } from './payments-review-utils';

/**
 * The rows of the review, and the chooser that opens under one of them.
 *
 * Its own component because three places show rows: a group the operator can act on, the panel that opens
 * on one policy when a file has several rows against it, and the groups under Unable to apply. They look
 * the same -- same line, policy, insured and file columns -- and the way two copies of a table stop looking
 * the same is that somebody fixes one of them. `mode="unable"` drops what cannot be acted on (the checkbox,
 * the payment columns, the action) and puts the reason in their place.
 *
 * Everything it needs comes through `reviewApi`, injected rather than passed: there are twenty-odd
 * helpers behind these cells, and twenty-odd props would be a worse description of the relationship
 * than "this is a view onto the review screen's state".
 */
export default {
  name: 'PaymentsReviewRows',
  components: {
    BaseButton, BaseTable, BaseTableColumn, BaseTooltip, InputSwitcher, PolicyNumberCell
  },
  inject: ['reviewApi'],
  props: {
    rows: {
      type: Array,
      required: true,
    },
    group: {
      type: Object,
      default: () => ({}),
    },
    /** `review` (the default) or `unable`: rows nothing on this screen can apply. */
    mode: {
      type: String,
      default: 'review',
    },
  },
  computed: {
    api() {
      return this.reviewApi();
    },
    unable() {
      return this.mode === 'unable';
    },
    // How many candidate-policy columns the rows need: the most candidates any row has, at most three.
    candidateColumns() {
      if (this.unable) return 0;
      const most = this.rows.reduce((n, row) => Math.max(n, (row.candidates || []).length), 0);
      return Math.min(most, 3);
    },
    // The payment columns mean nothing on a row that has no policy yet.
    showsPayment() {
      return !this.unable && this.candidateColumns === 0;
    },
    actionWidth() {
      return this.api.isCancelRun ? '150px' : '96px';
    },
  },
  methods: {
    nameMatchBadge,
    candidateHeader(n) {
      return this.api.runText('paymentsReview.candidatePolicy', {n}).toLocaleUpperCase();
    },
    // The candidates shown in column `index`: one each, and the last column takes the rest.
    candidatesAt(row, index) {
      const candidates = row.candidates || [];
      return index < 2 ? candidates.slice(index, index + 1) : candidates.slice(index);
    },
    holderDiffers(row, candidate) {
      return !!candidate.fullName && candidate.fullName !== this.api.insuredName(row);
    },
  },
};
</script>

<style lang="scss">
.review-name-match {
  align-self: flex-start;
  margin-top: 4px;
}

.after-label--written-off {
  color: #A32C2C;
}

.following-cell {
  display: flex;
  flex-basis: 100%;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &__label {
    font-size: 13px;
    line-height: 1.3;
  }
}

.review-candidate {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
